import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Stethoscope,
  ClipboardList,
  Clock,
  FileText,
  Mail,
  Phone,
  CheckCircle2,
} from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

const conditionsSeen = [
  "Parkinson's disease &amp; atypical parkinsonism",
  "Post-CVA (ischaemic and haemorrhagic) — aphasia, apraxia of speech, dysarthria, dysphagia",
  "Cognitive-communication change — TBI, mild cognitive impairment, dementia",
  "Primary progressive aphasia",
  "Multiple sclerosis &mdash; dysarthria, swallowing change",
  "ALS &mdash; speech preservation, AAC planning",
  "Head &amp; neck cancer rehabilitation (post-treatment)",
  "Vocal fold paresis / paralysis, muscle tension dysphonia",
  "Functional voice disorders",
];

const triggers = [
  {
    title: "Voice or speech change",
    body:
      "Soft voice, monotone, slurring, breathiness, reduced intelligibility — particularly in Parkinson's, post-stroke, MS, or post-extubation.",
  },
  {
    title: "Swallowing concerns",
    body:
      "Coughing or throat-clearing at meals, wet voice quality, weight loss, recurrent chest infections, or patient report of food sticking.",
  },
  {
    title: "Cognitive-communication change",
    body:
      "Word-finding difficulty, conversational breakdown, reduced executive function, or family-reported decline impacting daily life.",
  },
  {
    title: "Post-acute rehabilitation",
    body:
      "Continuation of in-patient SLT goals into the community — particularly after stroke, brain injury or head &amp; neck cancer.",
  },
];

const Header = () => (
  <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
      <Link to="/" data-testid="phys-back-home" className="flex items-center">
        <img
          src="/logo.jpg"
          alt="Neurological Communication Partners"
          className="h-14 w-auto rounded-xl ring-1 ring-black/5"
        />
      </Link>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#2D332F] transition"
      >
        <ArrowLeft size={14} strokeWidth={1.5} /> Home
      </Link>
    </div>
  </header>
);

const SectionEyebrow = ({ children, icon: Icon }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon ? (
      <Icon size={14} strokeWidth={1.5} className="text-[#739072]" />
    ) : null}
    <p className="text-xs uppercase tracking-[0.22em] text-[#739072]">
      {children}
    </p>
  </div>
);

export default function ForPhysicians() {
  usePageMeta({
    title: "For Referring Physicians — Clinical Referral Pathway",
    description:
      "Clinician-to-clinician adult speech-language pathology referral pathway. When to refer, how to refer, and turnaround times for Parkinson's, post-stroke, dysphagia, cognitive and voice care.",
    path: "/for-physicians",
  });
  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <Header />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
          For referring physicians
        </p>
        <h1
          data-testid="phys-title"
          className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
        >
          Direct, clinician-to-clinician
          <br />
          <em className="text-[#739072]">adult speech-language pathology.</em>
        </h1>
        <p className="mt-6 text-[#5C6661] text-lg leading-relaxed max-w-3xl">
          A concise reference for neurology, geriatric medicine, ENT, primary
          care and rehabilitation colleagues considering referral to
          Neurological Communication Partners. Kathleen McGrath, M.S. CCC-SLP
          is licensed in New Hampshire and Vermont (ASHA CCC-SLP) and in
          Ireland (CORU), and provides secure telehealth across these
          jurisdictions.
        </p>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16"
          data-testid="phys-conditions"
        >
          <SectionEyebrow icon={Stethoscope}>Conditions seen</SectionEyebrow>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-6">
            Clinical scope
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2.5">
            {conditionsSeen.map((c, i) => (
              <li
                key={i}
                data-testid={`phys-condition-${i}`}
                className="flex gap-3 text-[#2D332F]/85 leading-relaxed"
              >
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                <span dangerouslySetInnerHTML={{ __html: c }} />
              </li>
            ))}
          </ul>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
          data-testid="phys-triggers"
        >
          <SectionEyebrow icon={ClipboardList}>When to refer</SectionEyebrow>
          <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-6">
            Clinical triggers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {triggers.map((t, i) => (
              <div
                key={t.title}
                data-testid={`phys-trigger-${i}`}
                className="rounded-2xl border border-black/5 bg-white p-6"
              >
                <p
                  className="font-medium text-[#2D332F] mb-2"
                  dangerouslySetInnerHTML={{ __html: t.title }}
                />
                <p
                  className="text-sm text-[#5C6661] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: t.body }}
                />
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14"
        >
          <div data-testid="phys-howto">
            <SectionEyebrow icon={FileText}>How to refer</SectionEyebrow>
            <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-4">
              A short, encrypted email is enough.
            </h2>
            <ol className="list-decimal pl-5 space-y-2.5 text-[#2D332F]/85 leading-relaxed">
              <li>
                Email{" "}
                <a
                  href="mailto:neurocompartners@gmail.com?subject=Referral%20-%20[Patient%20initials]"
                  className="underline text-[#739072]"
                >
                  neurocompartners@gmail.com
                </a>{" "}
                with patient initials in the subject line.
              </li>
              <li>
                Send any relevant clinical summary, imaging report, or prior
                SLP / dietetic notes as encrypted PDF attachments. Avoid
                free-text PHI in the body.
              </li>
              <li>
                Kathleen will confirm receipt the same business day and offer
                the patient a 15-minute discovery call within 1&ndash;2
                business days.
              </li>
              <li>
                Initial evaluation report is returned to the referrer within
                <strong> 7 calendar days</strong> of the first session, with
                progress notes available on request.
              </li>
            </ol>
          </div>

          <div data-testid="phys-needs">
            <SectionEyebrow icon={ClipboardList}>What helps</SectionEyebrow>
            <h2 className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#2D332F] mb-4">
              Information that speeds up the first session.
            </h2>
            <ul className="space-y-2.5 text-[#2D332F]/85 leading-relaxed">
              <li className="flex gap-3">
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                Working diagnosis &amp; date of onset / event.
              </li>
              <li className="flex gap-3">
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                Relevant imaging (CT / MRI / FEES / VFSS) summary or full
                report.
              </li>
              <li className="flex gap-3">
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                Current medication list — especially neurological &amp;
                anticholinergic agents.
              </li>
              <li className="flex gap-3">
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                Previous SLT or dietetic input &amp; outcomes (if any).
              </li>
              <li className="flex gap-3">
                <CheckCircle2
                  size={16}
                  strokeWidth={1.6}
                  className="text-[#739072] mt-1 shrink-0"
                />
                Specific clinical question or priority concern from the
                referring clinician.
              </li>
            </ul>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-5"
          data-testid="phys-turnaround"
        >
          <div className="rounded-2xl border border-black/5 bg-white p-6">
            <Clock size={18} strokeWidth={1.5} className="text-[#739072] mb-3" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-1.5">
              Receipt confirmed
            </p>
            <p className="font-serif text-2xl text-[#2D332F]">Same day</p>
            <p className="text-sm text-[#5C6661] mt-1">Business day, by email.</p>
          </div>
          <div className="rounded-2xl border border-black/5 bg-white p-6">
            <Clock size={18} strokeWidth={1.5} className="text-[#739072] mb-3" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-1.5">
              First session offered
            </p>
            <p className="font-serif text-2xl text-[#2D332F]">1–2 weeks</p>
            <p className="text-sm text-[#5C6661] mt-1">
              Urgent dysphagia: prioritised same week.
            </p>
          </div>
          <div className="rounded-2xl border border-black/5 bg-white p-6">
            <Clock size={18} strokeWidth={1.5} className="text-[#739072] mb-3" />
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-1.5">
              Initial report
            </p>
            <p className="font-serif text-2xl text-[#2D332F]">≤ 7 days</p>
            <p className="text-sm text-[#5C6661] mt-1">
              From first session, returned to referrer.
            </p>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20 rounded-3xl border border-black/5 bg-[#2D332F] text-[#FDFCF8] p-8 md:p-12"
          data-testid="phys-direct-contact"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#D4B996] mb-3">
            Direct clinician contact
          </p>
          <p className="font-serif text-2xl md:text-3xl font-light leading-snug mb-6 max-w-2xl">
            For clinical discussion before referral, Kathleen is happy to take
            a brief call.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="mailto:neurocompartners@gmail.com?subject=Clinician%20enquiry"
              data-testid="phys-cta-email"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FDFCF8] text-[#2D332F] text-sm hover:bg-[#D4B996] transition"
            >
              <Mail size={14} strokeWidth={1.5} />
              neurocompartners@gmail.com
            </a>
            <a
              href="tel:+15187955154"
              data-testid="phys-cta-phone-us"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/30 text-[#FDFCF8] text-sm hover:bg-white/10 transition"
            >
              <Phone size={14} strokeWidth={1.5} />
              +1 (518) 795-5154 · USA
            </a>
            <a
              href="tel:+353873773620"
              data-testid="phys-cta-phone-ie"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/30 text-[#FDFCF8] text-sm hover:bg-white/10 transition"
            >
              <Phone size={14} strokeWidth={1.5} />
              +353 87 377 3620 · Ireland
            </a>
          </div>
          <p className="mt-6 text-xs text-[#FDFCF8]/60 max-w-2xl leading-relaxed">
            Please do not include identifiable patient information in
            unencrypted email. Use patient initials only in the subject line;
            send clinical detail by encrypted PDF attachment.
          </p>
        </motion.section>

        <div className="mt-12 text-sm text-[#5C6661]">
          <Link
            to="/#contact"
            data-testid="phys-cta-back"
            className="inline-flex items-center gap-2 hover:text-[#2D332F] transition"
          >
            Back to main contact options
            <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </main>
    </div>
  );
}
